/**
 * Armenian Belot — auction (bazar) only: bids, passes, contra / recontra, kapot flag.
 * No play or scoring (packages C–E).
 *
 * **Contra / recontra (single source of truth):**
 * - Allowed in phase `bidding` only when there is an active bid (`highestBid` and `bidAuthorSeat`).
 *   `CONTRA` sets `contraMultiplier` 1→2, `RECONTRA` 2→4; phase stays `bidding`; `consecutivePasses`
 *   resets to 0 so three new passes are required to close the auction. Further bids must still be
 *   strictly stronger than `highestBid` (raising tens/trump continues after doubling).
 * - Allowed in phase `closed` with the same multipliers (legacy / post-close API); pass count is
 *   already final for that transition.
 * - Team eligibility and clockwise `currentTurnSeat` are enforced in `engine.js`, not here.
 * - At ×4, further recontra is rejected.
 *
 * @module game/belot/armenian/auction
 */

const { SUIT_MIN, SUIT_MAX } = require("./types");
const {
  AUCTION_SUIT_STRENGTH,
  AUCTION_NO_TRUMP_STRENGTH,
  AUCTION_ALL_TRUMP_STRENGTH,
} = require("./constants");

/** @typedef {'no_trump'} BelotNoTrump */
/** @typedef {'all_trump'} BelotAllTrump */
/** @typedef {BelotNoTrump|BelotAllTrump|0|1|2|3} BelotTrumpBid */

/**
 * @typedef {{
 *   tens: number,
 *   trump: BelotTrumpBid,
 *   kapot: boolean,
 * }} BelotAuctionBid
 */

/**
 * @typedef {'bidding'|'closed'|'redeal'} BelotAuctionPhase
 */

/**
 * @typedef {{
 *   phase: BelotAuctionPhase,
 *   highestBid: BelotAuctionBid | null,
 *   bidAuthorSeat: number | null,
 *   consecutivePasses: number,
 *   contraMultiplier: 1|2|4,
 * }} BelotAuctionState
 */

/**
 * @typedef {{ type: 'BID', seat: number, bid: BelotAuctionBid }} BelotAuctionCmdBid
 * @typedef {{ type: 'PASS', seat: number }} BelotAuctionCmdPass
 * @typedef {{ type: 'CONTRA', seat: number }} BelotAuctionCmdContra
 * @typedef {{ type: 'RECONTRA', seat: number }} BelotAuctionCmdRecontra
 * @typedef {BelotAuctionCmdBid|BelotAuctionCmdPass|BelotAuctionCmdContra|BelotAuctionCmdRecontra} BelotAuctionCommand
 */

/**
 * @typedef {{ type: 'AUCTION_ENDED', bid: BelotAuctionBid, bidAuthorSeat: number }} BelotAuctionEventEnded
 * @typedef {{ type: 'AUCTION_REDEAL' }} BelotAuctionEventRedeal
 * @typedef {{ type: 'CONTRA_APPLIED', multiplier: 2 }} BelotAuctionEventContra
 * @typedef {{ type: 'RECONTRA_APPLIED', multiplier: 4 }} BelotAuctionEventRecontra
 * @typedef {BelotAuctionEventEnded|BelotAuctionEventRedeal|BelotAuctionEventContra|BelotAuctionEventRecontra} BelotAuctionEvent
 */

const TRUMP_NO_TRUMP = "no_trump";
const TRUMP_ALL_TRUMP = "all_trump";

/** Contract obligation used in scoring when kapot is won (not for ordering normal bids). */
const KAPOT_CONTRACT_POINTS = 250;

/** Минимальная открывающая заявка (Базар): 10 «десятков» = 100 очков. Capot: tens=25 и kapot=true. */
const MIN_OPENING_TENS = 10;

/**
 * @param {BelotTrumpBid} trump
 * @returns {number}
 */
function trumpBidStrength(trump) {
  if (trump === TRUMP_NO_TRUMP) return AUCTION_NO_TRUMP_STRENGTH;
  if (trump === TRUMP_ALL_TRUMP) return AUCTION_ALL_TRUMP_STRENGTH;
  if (
    typeof trump !== "number" ||
    trump < SUIT_MIN ||
    trump > SUIT_MAX ||
    !Number.isInteger(trump)
  ) {
    throw new TypeError("auction: invalid trump suit");
  }
  return AUCTION_SUIT_STRENGTH[trump];
}

/**
 * Total ordering for bids: higher `tens`, then stronger trump/no-trump, then kapot over non-kapot.
 * @param {BelotAuctionBid} a
 * @param {BelotAuctionBid} b
 * @returns {number} >0 if a stronger, <0 if b stronger, 0 if equal
 */
function compareBids(a, b) {
  if (a.tens !== b.tens) return a.tens - b.tens;
  const da = trumpBidStrength(a.trump);
  const db = trumpBidStrength(b.trump);
  if (da !== db) return da - db;
  const ka = a.kapot ? 1 : 0;
  const kb = b.kapot ? 1 : 0;
  return ka - kb;
}

/**
 * @param {BelotAuctionBid} next
 * @param {BelotAuctionBid|null} prev
 * @returns {boolean}
 */
function isStrictlyStrongerBid(next, prev) {
  if (prev == null) return true;
  return compareBids(next, prev) > 0;
}

/**
 * @param {BelotAuctionBid} bid
 * @returns {void}
 */
function assertValidBidShape(bid) {
  if (!bid || typeof bid !== "object") throw new TypeError("auction: bid object required");
  const { tens, trump, kapot } = bid;
  if (!Number.isInteger(tens)) {
    throw new Error("auction: bid.tens must be an integer");
  }
  const k = Boolean(kapot);
  if (tens < 10 || tens > 25) {
    throw new Error("auction: bid.tens must be between 10 and 25 (100–250 game points)");
  }
  if (tens === 25 && !k) {
    throw new Error("auction: tens=25 (Capot) requires kapot=true");
  }
  if (tens >= 10 && tens <= 24 && k) {
    throw new Error("auction: kapot uses tens=25 only");
  }
  if (trump === TRUMP_NO_TRUMP || trump === TRUMP_ALL_TRUMP) {
    /* ok */
  } else if (
    typeof trump === "number" &&
    Number.isInteger(trump) &&
    trump >= SUIT_MIN &&
    trump <= SUIT_MAX
  ) {
    /* ok */
  } else {
    throw new Error("auction: bid.trump must be no_trump, all_trump or suit 0..3");
  }
  if (kapot != null && typeof kapot !== "boolean") {
    throw new Error("auction: bid.kapot must be boolean");
  }
}

/**
 * @returns {BelotAuctionState}
 */
function createInitialAuctionState() {
  return {
    phase: "bidding",
    highestBid: null,
    bidAuthorSeat: null,
    consecutivePasses: 0,
    contraMultiplier: 1,
  };
}

/**
 * @param {BelotAuctionState} state
 * @param {BelotAuctionCommand} cmd
 * @returns {{ ok: true, state: BelotAuctionState, events: BelotAuctionEvent[] } | { ok: false, error: string, state: BelotAuctionState }}
 */
function applyAuctionCommand(state, cmd) {
  if (!state || typeof state !== "object") {
    return { ok: false, error: "auction: invalid state", state: /** @type {BelotAuctionState} */ (state) };
  }
  if (!cmd || typeof cmd.type !== "string") {
    return { ok: false, error: "auction: invalid command", state };
  }

  if (state.phase === "redeal") {
    return { ok: false, error: "auction: round already resolved (redeal)", state };
  }

  if (cmd.type === "CONTRA" || cmd.type === "RECONTRA") {
    const inBidding = state.phase === "bidding";
    const inClosed = state.phase === "closed";
    if (!inBidding && !inClosed) {
      return {
        ok: false,
        error: "auction: contra only during open bidding or after close",
        state,
      };
    }
    if (inBidding && (state.highestBid == null || state.bidAuthorSeat == null)) {
      return { ok: false, error: "auction: contra requires an active highest bid", state };
    }
    if (cmd.type === "CONTRA") {
      if (state.contraMultiplier !== 1) {
        return { ok: false, error: "auction: contra not allowed now", state };
      }
      const next = /** @type {BelotAuctionState} */ ({
        ...state,
        contraMultiplier: 2,
        ...(inBidding ? { consecutivePasses: 0 } : {}),
      });
      return {
        ok: true,
        state: next,
        events: [{ type: "CONTRA_APPLIED", multiplier: 2 }],
      };
    }
    if (state.contraMultiplier !== 2) {
      return { ok: false, error: "auction: recontra not allowed now", state };
    }
    const next = /** @type {BelotAuctionState} */ ({
      ...state,
      contraMultiplier: 4,
      ...(inBidding ? { consecutivePasses: 0 } : {}),
    });
    return {
      ok: true,
      state: next,
      events: [{ type: "RECONTRA_APPLIED", multiplier: 4 }],
    };
  }

  if (state.phase === "closed") {
    return { ok: false, error: "auction: bidding closed", state };
  }

  if (cmd.type === "PASS") {
    if (typeof cmd.seat !== "number" || !Number.isInteger(cmd.seat)) {
      return { ok: false, error: "auction: PASS requires integer seat", state };
    }
    const consecutivePasses = state.consecutivePasses + 1;
    if (consecutivePasses < 3) {
      return {
        ok: true,
        state: { ...state, consecutivePasses },
        events: [],
      };
    }
    if (state.highestBid == null || state.bidAuthorSeat == null) {
      const next = /** @type {BelotAuctionState} */ ({
        ...state,
        phase: "redeal",
        consecutivePasses: 0,
        contraMultiplier: 1,
      });
      return { ok: true, state: next, events: [{ type: "AUCTION_REDEAL" }] };
    }
    const next = /** @type {BelotAuctionState} */ ({
      ...state,
      phase: "closed",
      consecutivePasses: 0,
    });
    return {
      ok: true,
      state: next,
      events: [
        {
          type: "AUCTION_ENDED",
          bid: state.highestBid,
          bidAuthorSeat: state.bidAuthorSeat,
        },
      ],
    };
  }

  if (cmd.type === "BID") {
    if (typeof cmd.seat !== "number" || !Number.isInteger(cmd.seat)) {
      return { ok: false, error: "auction: BID requires integer seat", state };
    }
    try {
      assertValidBidShape(cmd.bid);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "auction: invalid bid";
      return { ok: false, error: msg, state };
    }
    const bid = {
      tens: cmd.bid.tens,
      trump: cmd.bid.trump,
      kapot: Boolean(cmd.bid.kapot),
    };
    if (state.highestBid == null && bid.tens < MIN_OPENING_TENS) {
      return {
        ok: false,
        error: `auction: opening bid must be at least tens=${MIN_OPENING_TENS} (100 points)`,
        state,
      };
    }
    if (!isStrictlyStrongerBid(bid, state.highestBid)) {
      return { ok: false, error: "auction: bid must be strictly stronger", state };
    }
    return {
      ok: true,
      state: {
        ...state,
        highestBid: bid,
        bidAuthorSeat: cmd.seat,
        consecutivePasses: 0,
      },
      events: [],
    };
  }

  return { ok: false, error: `auction: unknown command ${cmd.type}`, state };
}

module.exports = {
  TRUMP_NO_TRUMP,
  TRUMP_ALL_TRUMP,
  KAPOT_CONTRACT_POINTS,
  MIN_OPENING_TENS,
  trumpBidStrength,
  compareBids,
  isStrictlyStrongerBid,
  assertValidBidShape,
  createInitialAuctionState,
  applyAuctionCommand,
};
